import { type ReactElement } from 'react';
import { buildToolbarIconButtonSx } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { isLightTheme } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { Box, IconButton, Tooltip, useTheme } from '@/ui/primitives';
import { CloseFullscreenIcon, OpenInFullIcon } from '@/ui/primitives/icons';

const TOOLBAR_CONTROL_SIZE = 18;

type EntityDailyHistoryExpandControlsProps = {
  canCollapseAll: boolean;
  canExpandAll: boolean;
  entityLabel: string;
  onCollapseAll: () => void;
  onExpandAll: () => void;
};

export const EntityDailyHistoryExpandControls = ({
  canCollapseAll,
  canExpandAll,
  entityLabel,
  onCollapseAll,
  onExpandAll,
}: EntityDailyHistoryExpandControlsProps): ReactElement => {
  const theme = useTheme();
  const toolbarInactiveIconColor = isLightTheme(theme) ? 'text.secondary' : 'common.white';
  const toolbarIconButtonSx = buildToolbarIconButtonSx(
    toolbarInactiveIconColor,
    TOOLBAR_CONTROL_SIZE,
  );

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        width: 'fit-content',
      }}
    >
      <Tooltip title="Expand all" placement="top" arrow>
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <IconButton
            aria-label={`Expand all ${entityLabel.toLowerCase()} rows`}
            onClick={onExpandAll}
            disabled={!canExpandAll}
            sx={[
              toolbarIconButtonSx,
              {
                p: s('none'),
                minWidth: TOOLBAR_CONTROL_SIZE,
                maxWidth: TOOLBAR_CONTROL_SIZE,
                width: TOOLBAR_CONTROL_SIZE,
                height: TOOLBAR_CONTROL_SIZE,
              },
              !canExpandAll ? { opacity: 0.5 } : null,
            ]}
          >
            <OpenInFullIcon
              sx={{
                width: `${TOOLBAR_CONTROL_SIZE}px !important`,
                height: `${TOOLBAR_CONTROL_SIZE}px !important`,
              }}
            />
          </IconButton>
        </Box>
      </Tooltip>
      <Tooltip title="Collapse all" placement="top" arrow>
        <Box component="span" sx={{ display: 'inline-flex', ml: s('none') }}>
          <IconButton
            aria-label={`Collapse all ${entityLabel.toLowerCase()} rows`}
            onClick={onCollapseAll}
            disabled={!canCollapseAll}
            sx={[
              toolbarIconButtonSx,
              {
                p: s('none'),
                minWidth: TOOLBAR_CONTROL_SIZE,
                maxWidth: TOOLBAR_CONTROL_SIZE,
                width: TOOLBAR_CONTROL_SIZE,
                height: TOOLBAR_CONTROL_SIZE,
              },
              !canCollapseAll ? { opacity: 0.5 } : null,
            ]}
          >
            <CloseFullscreenIcon
              sx={{
                width: `${TOOLBAR_CONTROL_SIZE}px !important`,
                height: `${TOOLBAR_CONTROL_SIZE}px !important`,
              }}
            />
          </IconButton>
        </Box>
      </Tooltip>
    </Box>
  );
};
